import { Component, computed, inject, input, LOCALE_ID } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DmAvatar, DmIcon } from '@digitale-menschen/component-library-angular';
import { AvatarFromPersonDirective } from '../../directives/avatar-from-person.directive';
import { resolvePersonName } from '../../functions/person.function';
import { VerlaufTimelineGroup } from './verlauf-timeline-entry.model';
import { formatVerlaufTimelineTimestamp } from './verlauf-timeline-timestamp';

/**
 * The entries one user action produced, as a single block of the timeline:
 * removing a Funktion also drops the Standorte the remaining ones no longer
 * offer, and the two entries are parts of that one decision rather than two.
 *
 * Carries the marker, the rail, the author and the date for the whole block; the
 * entries are projected as `lp-verlauf-timeline-entry` in `compact` mode and
 * keep their own headline. The group deliberately has none of its own — no
 * single word covers every composition a save can produce.
 */
@Component({
  selector: 'lp-verlauf-timeline-group',
  imports: [DmAvatar, DmIcon, AvatarFromPersonDirective],
  templateUrl: './verlauf-timeline-group.component.html',
  styleUrl: './verlauf-timeline-group.component.scss',
  host: {
    class: 'lp-verlauf-timeline-group',
    '[class.lp-verlauf-timeline-group--last]': 'isLast()',
  },
})
export class VerlaufTimelineGroupComponent {
  public readonly group = input.required<VerlaufTimelineGroup>();
  /** The last block draws no connector below itself. */
  public readonly isLast = input<boolean>(false);

  protected readonly iconName = computed<string | null>(() => this.group().icon ?? null);
  protected readonly authorName = computed<string>(() => resolvePersonName(this.group().author));
  /** True where an icon occupies the marker column, so the avatar is free to lead the line. */
  protected readonly showsAuthorAvatar = computed<boolean>(() => !!this.iconName() && !!this.authorName());

  protected readonly timestampLabel = computed<string>(() => formatVerlaufTimelineTimestamp(this.group().timestamp, this.datePipe));

  private readonly datePipe = new DatePipe(inject(LOCALE_ID));
}
